/**
 * READING A SHOP'S WEEK, as pure functions with nothing imported.
 *
 * `.jsx` despite holding no JSX, because that extension is what the i18n checks
 * read: `check-i18n` and `check-translations` walk the `.jsx` files only, and
 * `hoursSummary` says "closed" and the name of every weekday. Called `.js`, this
 * file's sentences would have quietly left the dictionary's coverage — the
 * translation count dropped by one the moment it was written, which is how this
 * was noticed.
 *
 * A LEAF MODULE ON PURPOSE: these are wanted by the application form
 * (`shop.jsx`), by a shop's public page (`trader.jsx`) and by the screens that
 * arrange a meeting (`location.jsx`). Keeping them in `shop.jsx` made
 * location → shop → chrome → location a cycle, and a cycle between modules that
 * destructure the design system at evaluation time is a crash waiting for
 * whichever order the bundler happens to pick.
 *
 * READ, NEVER COMPARED. There was an `openAt` here that judged whether a shop
 * was open at a given moment, mirroring one in the api — and both are gone: a
 * trade at a counter now carries no time, so there is nothing to compare it
 * with. What is left turns a week into the sentence a shop would write on its
 * own door.
 */

export const DAYS = [['mon', "Monday"], ['tue', "Tuesday"], ['wed', "Wednesday"],
  ['thu', "Thursday"], ['fri', "Friday"], ['sat', "Saturday"], ['sun', "Sunday"]];

/**
 * WHICH SHOP A TRADE HAPPENS AT, if either side is one.
 *
 * THE SAME ORDER OF PREFERENCE as the server: the seller's counter first,
 * because that is where the card is, then the viewer's own if they are a shop.
 * A shop that did not tick "se puede quedar en tu mostrador" offers nothing and
 * is skipped, which is how the other side's counter gets to host instead.
 */
export function hostingShop(theirShop, myShop) {
  const offers = (s) => (s && s.name && s.counter !== false ? s : null);
  return offers(theirShop) || offers(myShop) || null;
}

/**
 * The week, written the way a sign on a door writes it.
 *
 * Consecutive days with the same hours are grouped, because "Mon–Fri 10:00–20:00"
 * is what a shop would say and seven lines is what a database would.
 */
export function hoursSummary(hours, t) {
  if (!hours) return null;
  const key = (d) => {
    const v = hours[d];
    return !v || v === 'closed' ? 'closed' : `${v.open}–${v.close}`;
  };
  const out = [];
  let i = 0;
  while (i < DAYS.length) {
    let j = i;
    while (j + 1 < DAYS.length && key(DAYS[j + 1][0]) === key(DAYS[i][0])) j += 1;
    const span = i === j ? t(DAYS[i][1]) : `${t(DAYS[i][1])}–${t(DAYS[j][1])}`;
    out.push(`${span} ${key(DAYS[i][0]) === 'closed' ? t("closed") : key(DAYS[i][0])}`);
    i = j + 1;
  }
  return out.join(' · ');
}

